import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import MapIcon from "@mui/icons-material/Map";
import { Button, Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";

import HelpModal from "../../../../components/HelpModal";
import PagesContainer from "../../../../components/UI/PagesContainer";
import useFetch from "../../../../hooks/useFetch";
import { ScheduleAppointmentService } from "../../appointment.service";
import {
  ETasyScheduleTypeCode,
  EcalendarStatusCode,
  IExamProcedure,
  IScheduleAppointment,
  ITasySchedCheckInRule,
  ITasySchedConfirmationRule,
} from "../../model";
import CalendarAddress from "../../CalendarAddress";
import ConfirmDate from "../../NewSchedule/Stepper/ConfirmDate";
import CancelScheduleButton from "./CancelSchedule/NewCancelScheduleButton";
import { Confirmation } from "./Confirmation";
import { ConsultData } from "./ConsultData";
import { Procedure } from "./Procedure";
import RescheduleButton from "./NewRescheduleButton";
import { ITicketAutomationReceivedRuleAllowedAction } from "../../../../services/tasySchedMenu.service";
import { getCancellationReasonOptions } from "./CancelSchedule/getCancellationReasonOptions";
import { useExtensionConfigs } from "../../../../hooks/useExtensionConfigs";
import { Extension } from "../../../../models/intalled-extensions";
import { getOrientations } from "../../../../services/orientations.service";
import { IOrientationItem, Orientations } from "./Orientations";

const TRASNLATION_PATH = "page.schedules.scheduleDetails.";

type ScheduleDetailsAppointment = IScheduleAppointment &
  Partial<IExamProcedure> & {
    orientation?: string;
  };

const NewScheduleDetails = () => {
  const [appointment, setAppointment] = useState<IScheduleAppointment>();

  const [checkInRule, setCheckInRule] = useState<ITasySchedCheckInRule | null>(
    null,
  );

  const [loading, setLoading] = useState(true);

  const { t } = useTranslation();

  const [confirmationRule, setConfirmationRule] =
    useState<ITasySchedConfirmationRule>();

  const [procedure, setProcedure] = useState<IExamProcedure>();

  const [addressConfig, setAddressConfig] = useState("");

  const [orientations, setOrientations] = useState<IOrientationItem[]>([]);

  const { identifier, tasySchedulingTypeCode } = useParams();
  const { pathname, state } = useLocation();
  const isNewScheduleFlow = pathname.startsWith("/schedule-new");
  const ticketAutomationReceivedRuleId = isNewScheduleFlow
    ? sessionStorage.getItem("scheduleNewMySchedulesRuleId")
    : null;

  const [allowedAction, setAllowedAction] =
    useState<ITicketAutomationReceivedRuleAllowedAction | null>(() => {
      const locationAllowedAction = (
        state as {
          allowedAction?: ITicketAutomationReceivedRuleAllowedAction | null;
        } | null
      )?.allowedAction;

      if (locationAllowedAction) return locationAllowedAction;
      if (!isNewScheduleFlow) return null;

      const record = sessionStorage.getItem("scheduleNewMySchedulesAction");
      return record ? JSON.parse(record) : null;
    });

  const { sendRequest } = useFetch(
    ScheduleAppointmentService.getConsulationScheduleAppointment,
  );

  const { sendRequest: examRequest } = useFetch(
    ScheduleAppointmentService.getExamScheduleAppointment,
  );

  const { sendRequest: getOrientationsRequest, loading: orientationsLoading } =
    useFetch(getOrientations);

  const { fetchExtensionConfig } = useExtensionConfigs();

  const isExam = useMemo(
    () => tasySchedulingTypeCode === ETasyScheduleTypeCode.EXAM,
    [tasySchedulingTypeCode],
  );

  const updateOrientations = useCallback(
    async (scheduleAppointment: ScheduleDetailsAppointment) => {
      const orientationUpdate: IOrientationItem[] = [];

      if (scheduleAppointment.orientation) {
        orientationUpdate.push({
          title: t(TRASNLATION_PATH + "orientationTitles.schedule"),
          content: scheduleAppointment.orientation,
        });
      }

      const { data: orientationsData, success: orientationsSuccess } =
        await getOrientationsRequest({
          insuranceCode: scheduleAppointment.insuranceCode,
          procedureInternalCode: scheduleAppointment.procedureInternalCode,
        });

      if (orientationsSuccess && orientationsData) {
        const orientationValues = [
          {
            title: t(TRASNLATION_PATH + "orientationTitles.procedure"),
            content: orientationsData.procedureOrientation,
          },
          {
            title: t(TRASNLATION_PATH + "orientationTitles.procedureInsurance"),
            content: orientationsData.procedureInsuranceOrientation,
          },
          {
            title: t(TRASNLATION_PATH + "orientationTitles.insurance"),
            content: orientationsData.insuranceObservation,
          },
        ].filter((item): item is IOrientationItem => Boolean(item.content));

        orientationUpdate.push(...orientationValues);
      }

      setOrientations(orientationUpdate);
    },
    [getOrientationsRequest, t],
  );

  const fetchConsult = useCallback(async () => {
    if (!identifier) return;
    const { data, success } = await sendRequest({
      identifier: Number(identifier),
      ticketAutomationReceivedRuleId,
    });
    if (success && data) {
      setAppointment(data.scheduleAppointment);
      setConfirmationRule(data.tasySchedConfirmationRule);
      setCheckInRule(data.tasySchedCheckInRule);
      await updateOrientations(data.scheduleAppointment);
    }
  }, [
    sendRequest,
    identifier,
    updateOrientations,
    ticketAutomationReceivedRuleId,
  ]);

  const fetchExam = useCallback(async () => {
    if (!identifier) return;
    const { data, success } = await examRequest({
      identifier: Number(identifier),
      ticketAutomationReceivedRuleId,
    });
    if (success && data) {
      setProcedure(data.scheduleAppointment);
      setAppointment(data.scheduleAppointment);
      setConfirmationRule(data.tasySchedConfirmationRule);
      await updateOrientations(data.scheduleAppointment);
    }
  }, [
    examRequest,
    identifier,
    updateOrientations,
    ticketAutomationReceivedRuleId,
  ]);

  const getData = useCallback(async () => {
    setLoading(true);
    if (isExam) {
      await fetchExam();
    } else {
      await fetchConsult();
    }
    setLoading(false);
  }, [isExam, fetchConsult, fetchExam]);

  const handleConfirm = useCallback(() => {
    setAppointment((old) => ({
      ...old!,
      calendarStatusCode: EcalendarStatusCode.CONFIMED,
      calendarStatusName: "Confirmado",
    }));
  }, []);

  useEffect(() => {
    getData();
  }, [getData]);

  const fetchAddress = useCallback(async () => {
    const configAddress = await fetchExtensionConfig(
      Extension.MY_SCHEDULES,
      "Address",
    );

    if (configAddress?.value) {
      setAddressConfig(configAddress.value);
    }
  }, [fetchExtensionConfig]);

  useEffect(() => {
    fetchAddress();
  }, [fetchAddress]);

  useEffect(() => {
    const locationAllowedAction = (
      state as {
        allowedAction?: ITicketAutomationReceivedRuleAllowedAction | null;
      } | null
    )?.allowedAction;

    if (!isNewScheduleFlow || !locationAllowedAction) return;

    sessionStorage.setItem(
      "scheduleNewMySchedulesAction",
      JSON.stringify(locationAllowedAction),
    );
    setAllowedAction(locationAllowedAction);
  }, [isNewScheduleFlow, state]);

  const isFutureSchedule = useMemo(
    () =>
      appointment &&
      dayjs(appointment.calendarStartDate || appointment.calendarDate).isAfter(
        new Date(),
      ),
    [appointment],
  );

  const canChangeSchedule = useMemo(
    () =>
      appointment &&
      isFutureSchedule &&
      appointment.calendarStatusCode !== EcalendarStatusCode.CANCELED,
    [appointment, isFutureSchedule],
  );

  const canConfirm = useMemo(
    () =>
      appointment &&
      isFutureSchedule &&
      appointment.calendarStatusCode === EcalendarStatusCode.NORMAL &&
      tasySchedulingTypeCode &&
      identifier &&
      (!isNewScheduleFlow || allowedAction?.confirm),
    [
      appointment,
      isFutureSchedule,
      tasySchedulingTypeCode,
      identifier,
      isNewScheduleFlow,
      allowedAction,
    ],
  );

  const canCancel = useMemo(
    () => canChangeSchedule && (!isNewScheduleFlow || allowedAction?.cancel),
    [canChangeSchedule, isNewScheduleFlow, allowedAction],
  );

  const canReschedule = useMemo(
    () =>
      canChangeSchedule && (!isNewScheduleFlow || allowedAction?.reschedule),
    [canChangeSchedule, isNewScheduleFlow, allowedAction],
  );

  const cancellationReasonOptions = useMemo(
    () =>
      appointment
        ? getCancellationReasonOptions(appointment, allowedAction)
        : undefined,
    [appointment, allowedAction],
  );

  const onClickAddToCalendar = useCallback(() => {
    const startDate = dayjs(appointment?.calendarStartDate);
    const endDate = dayjs(appointment?.calendarEndDate);
    const scheduleType =
      String(appointment?.tasyScheduleTypeCode) ===
      ETasyScheduleTypeCode.CONSULTATION
        ? t(TRASNLATION_PATH + "consultation")
        : t(TRASNLATION_PATH + "exam");

    const scheduleDate = startDate.format("DD/MM/YYYY");
    const scheduleTime = startDate.format("HH:mm");

    const event = {
      title: scheduleType,
      startDate: startDate.toISOString().replace(/[-:.]/g, ""),
      endDate: endDate.toISOString().replace(/[-:.]/g, ""),
      description: t(TRASNLATION_PATH + "schedule_description", {
        scheduleType,
        physicianName: appointment?.physicianNickName,
        date: scheduleDate,
        time: scheduleTime,
        interpolation: { escapeValue: false },
      }),
    };

    const icsData = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "BEGIN:VEVENT",
      `SUMMARY:${event.title}`,
      `DTSTART:${event.startDate}`,
      `DTEND:${event.endDate}`,
      `DESCRIPTION:${event.description}`,
      "END:VEVENT",
      "END:VCALENDAR",
    ].join("\n");

    const blob = new Blob([icsData], { type: "text/calendar;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "calendar.ics");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [appointment, t]);

  const loadingDetails = loading || orientationsLoading;

  return (
    <PagesContainer
      title={t("page.schedules.scheduleDetails.title")}
      loading={loadingDetails}
      sx={{
        gap: 2,
      }}
      rowActions={
        confirmationRule &&
        canConfirm && (
          <HelpModal
            title={t("page.schedules.scheduleDetails.confirmModal.title")}
            description={t(
              "page.schedules.scheduleDetails.confirmModal.description",
              { min: confirmationRule.time },
            )}
          />
        )
      }
    >
      <Stack
        height={"100%"}
        overflow={"auto"}
        justifyContent={"space-between"}
        gap={3}
      >
        <Stack gap={1}>
          {appointment && <ConsultData data={appointment} />}
          {appointment && (
            <CalendarAddress address={appointment.calendarAddress} />
          )}
          {procedure && <Procedure data={procedure} />}
          {appointment && (
            <ConfirmDate
              calendarStatusCode={appointment.calendarStatusCode}
              startHour={dayjs(appointment.calendarStartDate)}
              duration={appointment.minutesDuration}
              scheduleType={String(appointment.tasyScheduleTypeCode)}
              scheduleDate={appointment.calendarStartDate!}
              roomName={appointment.roomName}
              checkInRule={
                !isNewScheduleFlow || allowedAction?.checkIn
                  ? (checkInRule ?? undefined)
                  : undefined
              }
              encounterIdentifier={appointment.encounterIdentifier}
              identifier={Number(identifier)}
            />
          )}

          {appointment && (
            <Stack
              flexDirection={{ xs: "column", md: "row" }}
              spacing={{ xs: 2, md: 0 }}
              alignItems="center"
              justifyContent="space-between"
            >
              <Button
                component="a"
                href={addressConfig}
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                sx={{
                  width: { xs: "100%", md: "auto" },
                  gap: 1,
                  textTransform: "none",
                  py: 1.5,
                }}
              >
                <MapIcon />

                <Typography>{t(TRASNLATION_PATH + "view_on_map")}</Typography>
              </Button>

              {orientations.length > 0 && (
                <Orientations orientations={orientations} />
              )}

              <Button
                variant="contained"
                onClick={onClickAddToCalendar}
                sx={{
                  width: { xs: "100%", md: "auto" },
                  gap: 1,
                  textTransform: "none",
                  py: 1.5,
                }}
              >
                <CalendarMonthIcon />

                <Typography>
                  {t(TRASNLATION_PATH + "add_to_calendar")}
                </Typography>
              </Button>
            </Stack>
          )}
        </Stack>

        <Stack
          flexDirection={"row"}
          flexWrap={"wrap"}
          gap={2}
          alignSelf={"flex-end"}
          pb={1}
        >
          {canReschedule && (
            <RescheduleButton
              appointment={appointment!}
              tasySchedulingTypeCode={tasySchedulingTypeCode!}
              newSchedulePath="/schedule-new/menus"
              cancellationReasonOptions={cancellationReasonOptions}
              myAppointmentsRuleId={
                isNewScheduleFlow ? allowedAction?.id : undefined
              }
            />
          )}

          {canCancel && (
            <CancelScheduleButton
              appointment={appointment!}
              onCancelled={getData}
              tasySchedulingTypeCode={tasySchedulingTypeCode!}
              cancellationReasonOptions={cancellationReasonOptions}
            />
          )}

          {canConfirm && (
            <Confirmation
              onConfirmed={handleConfirm}
              identifier={Number(identifier)}
              identifierCalendar={appointment!.calendarIdentifier}
              type={tasySchedulingTypeCode!}
              rule={confirmationRule}
              date={appointment!.calendarDate}
            />
          )}
        </Stack>
      </Stack>
    </PagesContainer>
  );
};

export default NewScheduleDetails;
